/**
 * Centralized Lead Submission Service
 * Handles geolocation fetching and lead submission for all forms
 */

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object>} Geolocation response
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Geolocation fetch error:', error);
    return null;
  }
};

/**
 * Extract country code from geolocation data
 * @param {Object} geoData - Geolocation response
 * @returns {string} Country code (e.g., 'US', 'PK')
 */
export const getCountryFromGeo = (geoData) => {
  if (geoData && geoData.isSuccess && geoData.data) {
    return geoData.data.countryCode || '';
  }
  return '';
};

/**
 * Get dial code from country code
 * @param {string} countryCode - Two letter country code
 * @returns {number} Country dial code
 */
export const getDialCodeFromCountry = (countryCode) => {
  const dialCodes = {
    'US': 1, 'CA': 1, 'GB': 44, 'IN': 91, 'AU': 61, 'DE': 49, 
    'FR': 33, 'JP': 81, 'CN': 86, 'KR': 82, 'BR': 55, 'MX': 52,
    'IT': 39, 'ES': 34, 'NL': 31, 'SE': 46, 'CH': 41, 'SG': 65,
    'AE': 971, 'ZA': 27, 'NZ': 64, 'PK': 92
  };
  return dialCodes[countryCode] || 1;
};

/**
 * Submit lead to CRM
 * @param {Object} formData - Form data
 * @param {Object} geoData - Geolocation data
 * @param {string} pageSource - Source page identifier
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (formData, geoData, pageSource = 'website') => {
  try {
    // Extract numeric phone number only
    const phoneNumber = formData.phone.replace(/\D/g, '');
    
    // Extract country dial code (numeric only)
    const countryDialCode = formData.country_code 
      ? parseInt(formData.country_code.replace(/\D/g, ''))
      : 1;

    // Get country code from geolocation or use US as default
    const country = geoData && geoData.data 
      ? geoData.data.countryCode 
      : 'US';

    // Get IP address from geolocation
    const ipAddress = geoData && geoData.data 
      ? geoData.data.ipAddress 
      : '';

    // Get city and state from geolocation if available
    const city = geoData && geoData.data ? geoData.data.cityName || '' : '';
    const state = geoData && geoData.data ? geoData.data.regionName || '' : '';

    // Prepare geoLocation as JSON string
    const geoLocation = geoData ? JSON.stringify(geoData) : '';

    // Prepare payload
    const payload = {
      name: formData.name || '',
      email: formData.email || '',
      countryDialCode: countryDialCode,
      country: country,
      phoneNumber: phoneNumber,
      ipAddress: ipAddress,
      description: formData.message || '',
      pageUrl: window.location.href,
      city: city,
      state: state,
      geoLocation: geoLocation,
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    return {
      success: response.ok,
      statusCode: response.status,
      data: result
    };
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      statusCode: 500,
      data: {
        errorMessage: 'Network error occurred. Please try again.'
      }
    };
  }
};

/**
 * Handle lead submission response
 * @param {Object} response - API response
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
export const handleLeadResponse = (response, onSuccess, onError) => {
  const { statusCode, data } = response;

  if (statusCode === 200 && data.isSuccess && data.data && data.data.customerId) {
    // Success - redirect to application page
    const customerId = data.data.customerId;
    if (onSuccess) {
      onSuccess(customerId);
    } else {
      window.location.href = `/application/main?customerid=${customerId}`;
    }
  } else if (statusCode === 401) {
    // Unauthorized error
    const errorMsg = data.message || 'Unauthorized access';
    if (onError) {
      onError(errorMsg);
    }
  } else if (statusCode === 400) {
    // Bad request error
    const errorMsg = data.errorMessage || 'Invalid form data';
    if (onError) {
      onError(errorMsg);
    }
  } else {
    // Other errors
    const errorMsg = data.errorMessage || data.message || 'An error occurred. Please try again.';
    if (onError) {
      onError(errorMsg);
    }
  }
};
